import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('garageToken'));
  const [loading, setLoading] = useState(true);
  const [alertCount, setAlertCount] = useState(0);

  // Load user from token on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('garageToken');
      if (storedToken) {
        try {
          const res = await authAPI.getMe();
          setUser(res.data.data);
        } catch {
          localStorage.removeItem('garageToken');
          localStorage.removeItem('garageUser');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { token: newToken, data: userData } = res.data;
    localStorage.setItem('garageToken', newToken);
    localStorage.setItem('garageUser', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    toast.success(`Welcome back, ${userData.name}!`);
    return userData;
  }, []);

  const register = useCallback(async (formData) => {
    const res = await authAPI.register(formData);
    const { token: newToken, data: userData } = res.data;
    localStorage.setItem('garageToken', newToken);
    localStorage.setItem('garageUser', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    toast.success('Account created successfully!');
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('garageToken');
    localStorage.removeItem('garageUser');
    setToken(null);
    setUser(null);
    toast.success('Logged out successfully.');
  }, []);

  const isAdmin = user?.role === 'admin';
  const isMechanic = user?.role === 'mechanic';
  const isCustomer = user?.role === 'customer';
  const isStaff = isAdmin || isMechanic;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        isAdmin,
        isMechanic,
        isCustomer,
        isStaff,
        alertCount,
        setAlertCount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;